// Foundayo (orforglipron) monthly review decisions. Source: AHT Foundayo PGD v2.1 (Monitoring; Cautions/Need for
// further advice; Dosage & Frequency; Re-initiation criteria) and change list rows FDY19 to FDY21.
// Pure functions: the review form renders what these return and never decides clinically itself.

export const FOUNDAYO_DOSE_STEPS = ["fdy-0_8mg", "fdy-2_5mg", "fdy-5_5mg", "fdy-9mg", "fdy-14_5mg", "fdy-17_2mg"] as const;
export type FoundayoDoseStep = (typeof FOUNDAYO_DOSE_STEPS)[number];

export const FOUNDAYO_DOSE_LABELS: Record<FoundayoDoseStep, string> = {
  "fdy-0_8mg": "0.8 mg",
  "fdy-2_5mg": "2.5 mg",
  "fdy-5_5mg": "5.5 mg",
  "fdy-9mg": "9 mg",
  "fdy-14_5mg": "14.5 mg",
  "fdy-17_2mg": "17.2 mg",
};

/** PGD: BMI below 22.5 while on treatment stops it. The floor value itself is allowed. */
export const FOUNDAYO_BMI_FLOOR = 22.5;
/** PGD: at least 30 days at the current dose before any increase. */
export const FOUNDAYO_MIN_DAYS_AT_DOSE = 30;
/** PGD: stop if less than 5% weight loss from the initial body weight after 6 months at the maximum tolerated dose. */
export const FOUNDAYO_EFFICACY_MIN_PERCENT = 5;
export const FOUNDAYO_EFFICACY_MONTHS = 6;
/** PGD: waist at least every 3 months (change list FDY19). */
export const FOUNDAYO_WAIST_INTERVAL_DAYS = 91;
/** PGD: HbA1c at least every 6 months for type 2 diabetes. */
export const FOUNDAYO_HBA1C_INTERVAL_DAYS = 183;
/** Maximum dose with a strong CYP3A4 inhibitor or clinical OATP1B inhibitor. */
export const FOUNDAYO_CAPPED_MAX_STEP: FoundayoDoseStep = "fdy-9mg";

export type FoundayoStopReason =
  | "bmi_below_floor"
  | "suspected_pancreatitis"
  | "confirmed_pancreatitis"
  | "self_harm_thoughts"
  | "naion_confirmed"
  | "pregnancy"
  | "efficacy_below_threshold"
  | "disordered_eating_features"
  | "contraception_unwilling_or_unable";

export type FoundayoReferral =
  | "same_day_emergency_assessment"
  | "same_day_medical_assessment"
  | "same_day_eye_assessment"
  | "same_day_assessment_fluids"
  | "gp_gallbladder"
  | "gp_same_day_gallbladder"
  | "gp_tachycardia"
  | "gp_hypotension"
  | "gp_eating_disorder_with_consent";

export interface FoundayoReviewInput {
  bmi: number | null;
  initialWeightKg: number | null;
  currentWeightKg: number | null;
  restingPulse: number | null;
  /** BP is recorded at every review for everyone (AD15). */
  bloodPressureRecorded: boolean;
  pulsePersistentlyAbove100: boolean;
  symptomaticHypotension: boolean;
  suspectedPancreatitis: boolean;
  confirmedPancreatitis: boolean;
  selfHarmThoughts: boolean;
  suddenVisionChange: boolean;
  naionConfirmed: boolean;
  cannotKeepFluidsDown: boolean;
  gallbladderSymptoms: boolean;
  gallbladderRedFlags: boolean;
  pregnant: boolean;
  /** Null until answered. Unwilling or unable to use effective contraception is an exclusion. */
  contraceptionStatus: "not_applicable" | "effective_in_place" | "unwilling_or_unable" | null;
  /** New or worsening low mood, anxiety or unusual change in behaviour reported. */
  moodConcern: boolean;
  medicinesReconciled: boolean;
  disorderedEatingFeatures: boolean;
  /** The four direct screening questions (PGD): all must be answered at every review. */
  eatingScreen: { bingeEating: boolean | null; selfInducedVomiting: boolean | null; laxativeOrDiureticMisuse: boolean | null; compulsiveExercise: boolean | null };
  type2Diabetes: boolean;
  lastHba1cDate: string | null;
  lastWaistDate: string | null;
  /** Date the recorded maximum tolerated dose was set, if any. */
  maxToleratedDoseDate: string | null;
  now?: Date;
}

export interface FoundayoReviewResult {
  stopSupply: boolean;
  stopReasons: FoundayoStopReason[];
  referrals: FoundayoReferral[];
  /** Permanent flag: acute pancreatitis confirmed. Never restart. */
  neverRestart: boolean;
  gpNotifiedSameDay: boolean;
  /** Review cannot be completed until these are answered. */
  blockers: string[];
  flags: string[];
  percentChangeFromInitial: number | null;
  efficacyReviewDue: boolean;
}

const DAY_MS = 86_400_000;

function parseIso(value: string | null): number | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const t = Date.parse(value.slice(0, 10) + "T00:00:00Z");
  return Number.isFinite(t) ? t : null;
}

function daysBetween(fromIso: string | null, now: Date): number | null {
  const from = parseIso(fromIso);
  if (from === null) return null;
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - from) / DAY_MS);
}

/** Percentage weight change against the FIXED initial weight recorded at first supply, never the last review. Negative is loss. */
export function foundayoPercentChange(initialKg: number | null, currentKg: number | null): number | null {
  if (initialKg === null || currentKg === null || !(initialKg > 0) || !(currentKg > 0)) return null;
  return ((currentKg - initialKg) / initialKg) * 100;
}

/** Six months at the recorded maximum tolerated dose has elapsed. A missing date means the clock has not started. */
export function foundayoEfficacyReviewDue(maxToleratedDoseDate: string | null, now: Date = new Date()): boolean {
  const start = parseIso(maxToleratedDoseDate);
  if (start === null) return false;
  const due = new Date(start);
  due.setUTCMonth(due.getUTCMonth() + FOUNDAYO_EFFICACY_MONTHS);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return today >= due.getTime();
}

export function evaluateFoundayoReview(input: FoundayoReviewInput): FoundayoReviewResult {
  const now = input.now ?? new Date();
  const stopReasons: FoundayoStopReason[] = [];
  const referrals: FoundayoReferral[] = [];
  const blockers: string[] = [];
  const flags: string[] = [];

  // Fail safe: a missing BMI cannot be shown to be at or above the floor.
  if (input.bmi === null || !Number.isFinite(input.bmi)) blockers.push("Record a verified BMI before the review can be completed.");
  else if (input.bmi < FOUNDAYO_BMI_FLOOR) stopReasons.push("bmi_below_floor");

  if (input.currentWeightKg === null) blockers.push("Record a verified weight.");
  if (input.initialWeightKg === null) blockers.push("The initial weight recorded at first supply is missing.");
  if (input.restingPulse === null) blockers.push("Record the resting pulse.");
  if (!input.bloodPressureRecorded) blockers.push("Record blood pressure.");
  if (!input.medicinesReconciled) blockers.push("Complete the full medicines reconciliation.");
  if (input.contraceptionStatus === null) blockers.push("Record pregnancy and contraception status.");
  if (input.contraceptionStatus === "unwilling_or_unable") stopReasons.push("contraception_unwilling_or_unable");
  if (input.moodConcern) flags.push("mood_concern_review_and_gp_referral");

  const screen = input.eatingScreen;
  if ([screen.bingeEating, screen.selfInducedVomiting, screen.laxativeOrDiureticMisuse, screen.compulsiveExercise].some((a) => a === null)) {
    blockers.push("Answer all four disordered eating screening questions.");
  }
  const screenPositive =
    screen.bingeEating === true || screen.selfInducedVomiting === true || screen.laxativeOrDiureticMisuse === true || screen.compulsiveExercise === true;
  if (input.disorderedEatingFeatures || screenPositive) {
    stopReasons.push("disordered_eating_features");
    referrals.push("gp_eating_disorder_with_consent");
  }

  if (input.suspectedPancreatitis || input.confirmedPancreatitis) {
    stopReasons.push(input.confirmedPancreatitis ? "confirmed_pancreatitis" : "suspected_pancreatitis");
    referrals.push("same_day_emergency_assessment");
  }
  if (input.selfHarmThoughts) {
    stopReasons.push("self_harm_thoughts");
    referrals.push("same_day_medical_assessment");
  }
  if (input.suddenVisionChange || input.naionConfirmed) referrals.push("same_day_eye_assessment");
  if (input.naionConfirmed) stopReasons.push("naion_confirmed");
  if (input.cannotKeepFluidsDown) referrals.push("same_day_assessment_fluids");
  if (input.gallbladderSymptoms) referrals.push(input.gallbladderRedFlags ? "gp_same_day_gallbladder" : "gp_gallbladder");
  if (input.pulsePersistentlyAbove100) referrals.push("gp_tachycardia");
  if (input.symptomaticHypotension) referrals.push("gp_hypotension");
  if (input.pregnant) stopReasons.push("pregnancy");

  const percent = foundayoPercentChange(input.initialWeightKg, input.currentWeightKg);
  const efficacyDue = foundayoEfficacyReviewDue(input.maxToleratedDoseDate, now);
  if (efficacyDue) {
    if (percent === null) blockers.push("Efficacy review is due: a verified current weight is needed.");
    else if (-percent < FOUNDAYO_EFFICACY_MIN_PERCENT) stopReasons.push("efficacy_below_threshold");
  }

  if (input.type2Diabetes) {
    const days = daysBetween(input.lastHba1cDate, now);
    if (days === null || days > FOUNDAYO_HBA1C_INTERVAL_DAYS) flags.push("hba1c_over_6_months_old");
  }
  const waistDays = daysBetween(input.lastWaistDate, now);
  if (waistDays === null || waistDays > FOUNDAYO_WAIST_INTERVAL_DAYS) flags.push("waist_due");

  return {
    stopSupply: stopReasons.length > 0,
    stopReasons: [...new Set(stopReasons)],
    referrals: [...new Set(referrals)],
    neverRestart: input.confirmedPancreatitis,
    gpNotifiedSameDay: input.suspectedPancreatitis || input.confirmedPancreatitis,
    blockers,
    flags,
    percentChangeFromInitial: percent,
    efficacyReviewDue: efficacyDue,
  };
}

export type FoundayoDoseDecision = "hold" | "next_step" | "reduce";

export interface FoundayoDoseOption {
  decision: FoundayoDoseDecision;
  step: FoundayoDoseStep;
}

/**
 * Offers only hold, the next step, or one step down; never a skip. The next step needs 30 days at the current dose and
 * respects the 9 mg cap. Returns the options and, when the increase is blocked, the date it becomes permitted.
 */
export function foundayoDoseOptions(
  currentStep: FoundayoDoseStep,
  doseStartDate: string | null,
  cappedAt9mg: boolean,
  now: Date = new Date(),
): { options: FoundayoDoseOption[]; nextIncreaseBlockedUntil: string | null; nextIncreaseBlockedByCap: boolean } {
  const i = FOUNDAYO_DOSE_STEPS.indexOf(currentStep);
  const options: FoundayoDoseOption[] = [{ decision: "hold", step: currentStep }];
  if (i > 0) options.push({ decision: "reduce", step: FOUNDAYO_DOSE_STEPS[i - 1]! });

  let blockedUntil: string | null = null;
  let blockedByCap = false;
  const next = FOUNDAYO_DOSE_STEPS[i + 1];
  if (next !== undefined) {
    if (cappedAt9mg && i + 1 > FOUNDAYO_DOSE_STEPS.indexOf(FOUNDAYO_CAPPED_MAX_STEP)) {
      blockedByCap = true;
    } else {
      const start = parseIso(doseStartDate);
      const days = daysBetween(doseStartDate, now);
      // Fail safe: an unknown start date cannot show 30 days have passed, so no increase is offered.
      if (start !== null && days !== null && days >= FOUNDAYO_MIN_DAYS_AT_DOSE) options.push({ decision: "next_step", step: next });
      else if (start !== null) blockedUntil = new Date(start + FOUNDAYO_MIN_DAYS_AT_DOSE * DAY_MS).toISOString().slice(0, 10);
    }
  }
  return { options, nextIncreaseBlockedUntil: blockedUntil, nextIncreaseBlockedByCap: blockedByCap };
}

export type MaxToleratedReason = "at_17_2mg" | "failed_increase" | "agreed_hold_adverse_effects" | "held_for_interacting_medicine";

/**
 * The maximum tolerated dose may only be recorded at 17.2 mg, after a failed increase, or an agreed hold for adverse effects.
 * A dose held solely because of an interacting medicine is a temporary cap and never starts the six month clock.
 */
export function canRecordMaxToleratedDose(step: FoundayoDoseStep, reason: MaxToleratedReason): boolean {
  if (reason === "held_for_interacting_medicine") return false;
  if (reason === "at_17_2mg") return step === "fdy-17_2mg";
  return reason === "failed_increase" || reason === "agreed_hold_adverse_effects";
}

// ---------------------------------------------------------------------------
// Review method and GP notification (PGD v2.1: Consultation and verification; GP notification and information sharing).
// ---------------------------------------------------------------------------

export type FoundayoReviewMethod = "live_video" | "in_person";
export type FoundayoMeasurementMethod = "in_person_premises" | "live_video_observed" | "gp_or_hcp_supplied";
export type FoundayoGpConsent = "given" | "declined_or_no_gp";
export type FoundayoGpNotifyMethod = "template_letter" | "secure_email" | "other_secure";

/** PGD: notify the GP on initiation, at every dose change, on discontinuation and on any significant adverse reaction (where consent is given). */
export function foundayoGpNotificationRequired(doseDecision: string, stopSupply: boolean): boolean {
  return stopSupply || doseDecision === "stop" || doseDecision === "next_step" || doseDecision === "reduce";
}

export interface FoundayoRecordInput {
  reviewMethod: "" | FoundayoReviewMethod;
  measurementMethod: "" | FoundayoMeasurementMethod;
  doseDecision: string;
  stopSupply: boolean;
  gpConsent: "" | FoundayoGpConsent;
  gpNotifyMethod: "" | FoundayoGpNotifyMethod;
  gpNotifyDate: string;
  gpDeclineReason: string;
  now?: Date;
}

/** Reasons the review cannot be saved yet. Nothing is assumed: every field here must be answered. */
export function foundayoRecordBlockers(input: FoundayoRecordInput): string[] {
  const now = input.now ?? new Date();
  const blockers: string[] = [];
  if (input.reviewMethod === "") {
    blockers.push("Record how this review was carried out: live video or in person. A questionnaire alone is not enough.");
  }
  if (input.measurementMethod === "") {
    blockers.push("Record how height and weight were verified. Self-declared weight is not accepted.");
  }
  if (foundayoGpNotificationRequired(input.doseDecision, input.stopSupply)) {
    if (input.gpConsent === "") {
      blockers.push("This is a dose change or discontinuation: record whether the patient consented to GP notification.");
    } else if (input.gpConsent === "given") {
      if (input.gpNotifyMethod === "") blockers.push("Record how the GP was notified.");
      const when = parseIso(input.gpNotifyDate || null);
      const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      if (when === null) blockers.push("Record the date the GP was notified.");
      else if (when > today) blockers.push("The GP notification date cannot be in the future.");
    } else if (input.gpDeclineReason.trim() === "") {
      blockers.push("Record the reason GP notification was declined (or there is no GP), the risks considered, and why supply was still considered safe or the decision not to supply.");
    }
  }
  return blockers;
}
